import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Menu, X, Facebook, Instagram } from 'lucide-react';
import { Button } from './ui/button';
import { companyInfo } from '../data/mock';

const Header = ({ activeSection, onNavigate }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { id: 'home', label: 'Home', isSection: true },
    { id: 'services', label: 'Services', isSection: true },
    { id: 'all-services', label: 'Heating & Cooling', link: '/services/all' },
    { id: 'about', label: 'About', isSection: true },
    { id: 'gallery', label: 'Gallery', isSection: true },
    { id: 'faq', label: 'FAQ', link: '/faq' },
    { id: 'maintenance-plans', label: 'Maintenance Plans', link: '/maintenance-plans' },
    { id: 'blog', label: 'Blog', link: '/blog' },
    { id: 'contact', label: 'Contact', isSection: true }
  ];

  const handleNavClick = (sectionId) => {
    onNavigate(sectionId);
    setIsMobileMenuOpen(false);
  };

  return (
    <header
      className={`fixed top-10 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-md shadow-lg py-2'
          : 'bg-transparent py-4'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => handleNavClick('home')}>
            <img
              src={companyInfo.logo}
              alt={companyInfo.name}
              className="h-28 w-28 object-contain rounded-lg shadow-md"
            />
            <div className="hidden sm:block">
              <h1 className={`font-bold text-lg romano-heading transition-colors ${
                isScrolled ? 'text-[#622B62]' : 'text-white'
              }`}>
                Romano
              </h1>
              <p className={`text-xs transition-colors ${
                isScrolled ? 'text-[#622B62]/70' : 'text-white/80'
              }`}>
                Heating & Cooling
              </p>
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            {navItems.map((item) => (
              item.link ? (
                <Link
                  key={item.id}
                  to={item.link}
                  className={`nav-link font-medium transition-colors ${
                    isScrolled ? 'text-[#622B62] hover:text-[#DA9C3A]' : 'text-white hover:text-[#DA9C3A]'
                  }`}
                >
                  {item.label}
                </Link>
              ) : (
                <button
                  key={item.id}
                  onClick={() => handleNavClick(item.id)}
                  className={`nav-link font-medium transition-colors ${
                    activeSection === item.id
                      ? isScrolled ? 'text-[#DA9C3A]' : 'text-[#DA9C3A]'
                      : isScrolled ? 'text-[#622B62] hover:text-[#DA9C3A]' : 'text-white hover:text-[#DA9C3A]'
                  }`}
                >
                  {item.label}
                </button>
              )
            ))}
          </nav>

          {/* CTA & Social */}
          <div className="hidden md:flex items-center gap-4">
            <div className="flex items-center gap-2">
              <a
                href={companyInfo.socialLinks.facebook}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => { e.preventDefault(); window.open(companyInfo.socialLinks.facebook, '_blank') || (window.location.href = companyInfo.socialLinks.facebook); }}
                className={`p-2 rounded-full transition-colors ${
                  isScrolled
                    ? 'text-[#622B62] hover:bg-[#622B62]/10'
                    : 'text-white hover:bg-white/10'
                }`}
              >
                <Facebook size={18} />
              </a>
              <a
                href={companyInfo.socialLinks.instagram}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => { e.preventDefault(); window.open(companyInfo.socialLinks.instagram, '_blank') || (window.location.href = companyInfo.socialLinks.instagram); }}
                className={`p-2 rounded-full transition-colors ${
                  isScrolled
                    ? 'text-[#622B62] hover:bg-[#622B62]/10'
                    : 'text-white hover:bg-white/10'
                }`}
              >
                <Instagram size={18} />
              </a>
            </div>
            <a href={`tel:${companyInfo.phone}`}>
              <Button className="bg-[#DA9C3A] hover:bg-[#C48A2A] text-white font-semibold px-5 py-2 rounded-full shadow-lg hover:shadow-xl transition-all">
                <Phone size={16} className="mr-2" />
                {companyInfo.phone}
              </Button>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 rounded-lg transition-colors"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? (
              <X size={28} className={isScrolled ? 'text-[#622B62]' : 'text-white'} />
            ) : (
              <Menu size={28} className={isScrolled ? 'text-[#622B62]' : 'text-white'} />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden mt-4 pb-4 animate-fade-in">
            <nav className="flex flex-col gap-2">
              {navItems.map((item) => (
                item.link ? (
                  <Link
                    key={item.id}
                    to={item.link}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`py-3 px-4 rounded-lg text-left font-medium transition-colors ${
                      isScrolled
                        ? 'text-[#622B62] hover:bg-[#622B62]/10'
                        : 'text-white hover:bg-white/10'
                    }`}
                  >
                    {item.label}
                  </Link>
                ) : (
                  <button
                    key={item.id}
                    onClick={() => handleNavClick(item.id)}
                    className={`py-3 px-4 rounded-lg text-left font-medium transition-colors ${
                      activeSection === item.id
                        ? 'bg-[#622B62] text-white'
                        : isScrolled
                        ? 'text-[#622B62] hover:bg-[#622B62]/10'
                        : 'text-white hover:bg-white/10'
                    }`}
                  >
                    {item.label}
                  </button>
                )
              ))}
            </nav>
            <div className="mt-4 pt-4 border-t border-white/20">
              <a href={`tel:${companyInfo.phone}`} className="block">
                <Button className="w-full bg-[#DA9C3A] hover:bg-[#C48A2A] text-white font-semibold py-3 rounded-full">
                  <Phone size={18} className="mr-2" />
                  Call {companyInfo.phone}
                </Button>
              </a>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
